/**
 * AD-012's propeller effect, as a scene object: two `InstancedMesh`es — the
 * shimmer disc and the blade silhouettes — carrying one instance per aircraft
 * in the sky, player and opponents alike.
 *
 * **A port extension, and cosmetic only.** The original draws no propeller in
 * any view (AD-012's opening line), so nothing here is recovered behaviour.
 * It reads poses and `SimState` fields and writes nothing back: no hash, no
 * snapshot, no encounter state. Switching it off leaves the flight identical.
 *
 * **Two draw calls, whatever is flying.** Both meshes share one unit-radius
 * disc geometry and one pair of per-instance attributes (`phase`, `rpm`); the
 * per-aircraft hub transform and disc radius ride the instance matrix. A
 * fifteen-aeroplane raid costs exactly what a duel does.
 *
 * **Where the disc sits.** `packages/baker/src/propeller-hub.ts` measures the
 * hub off the same baked LOD the aeroplane is drawn from, in the same
 * recentred frame `flight/cockpit-view.ts`'s eye offsets use — so the disc
 * follows the airframe through every attitude with no per-frame knowledge of
 * the model at all. Hubs are fetched and measured once per model id per
 * document (`aircraft-mesh.ts`'s `propellerHubOf`); an aeroplane whose hub has
 * not arrived yet simply draws no disc for a frame or two.
 *
 * **Airframes with no nose propeller are skipped by name.** `FE2` (a pusher),
 * `GOTHA` (twin pushers on wing nacelles) and `HANDLEY` (twin tractors, also
 * on nacelles) have no propeller at the nose to draw, and a measured nose hub
 * would put one in the wrong place. AD-012's constraint calls for exactly this
 * to be checked against the fleet and documented rather than assumed; nacelle
 * propellers are out of scope for the effect as specified. The roster itself
 * lives in `modelled-propeller.ts`, because it is also what decides whether an
 * enhanced `.glb` draws the propeller it was built with.
 */

import type { Pose } from "@red-opus/engine";
import type { PropellerHub } from "@red-opus/baker/schema";
import * as THREE from "three/webgpu";
import { createPropDiscMaterials, PROP_PHASE_ATTRIBUTE, PROP_RPM_ATTRIBUTE, type PropDiscMaterials } from "../three/prop-disc-material.ts";
import type { BakedPaletteBody } from "../three/ramp-shade.ts";
import { propellerHubOf } from "./aircraft-mesh.ts";
import { hasNoseProp } from "./modelled-propeller.ts";
import { propellerPhaseStep } from "./propeller-rpm.ts";

// Re-exported because this is where it has always been read from — the roster
// rule now lives beside the *other* half of the same decision
// (`modelled-propeller.ts`): which airframes the effect draws for is exactly
// which airframes hide the `.glb`'s own blade.
export { hasNoseProp } from "./modelled-propeller.ts";

/** Aeroplanes one scene ever draws a propeller for at once — a raid's roster with headroom. */
const DISC_CAPACITY = 32;

/** Segments around the unit disc. 48 is smooth at gunsight range and costs 48 triangles once. */
const DISC_SEGMENTS = 48;

/**
 * Peak alpha in the external views (chase, fly-by, tower, VCR playback). Low
 * enough that the aeroplane behind it reads through, high enough that a
 * stopped propeller is unmistakable next to a spinning one.
 */
export const DISC_ALPHA_EXTERNAL = 0.32;
export const BLADE_ALPHA_EXTERNAL = 0.85;

/**
 * AD-012 §4's cockpit cap. From the pilot's seat the disc lies directly across
 * the gunsight picture, so both regimes run at roughly a third of their
 * external alpha: measured on a WebGPU readback, a target's contrast against
 * the sky at the boresight retains 88 % of its no-disc value through the
 * shimmer disc (see `apps/web/test/propeller-disc.test.ts` and the AD's
 * implementation note). The two numbers differ because a near-stopped blade is
 * a solid object the pilot should see, not a haze.
 */
export const DISC_ALPHA_COCKPIT = 0.12;
export const BLADE_ALPHA_COCKPIT = 0.4;

/** One aeroplane's contribution for this frame. */
export interface PropellerSource {
  /** Stable identity, so the integrated phase follows the right aeroplane across frames. */
  readonly id: string;
  /** `data/baked/models/<id>` — `null` for an airframe with no baked geometry. */
  readonly modelId: string | null;
  readonly pose: Pose;
  /** 0..1, from `propeller-rpm.ts`. */
  readonly rpmFraction: number;
  /**
   * Radius factor applied only while the cockpit camera is up. The player's
   * own source carries `PROP_RADIUS_COCKPIT_SCALE`: from the pilot's seat the
   * full measured disc dominated the windshield (user feedback, 2026-08-21 —
   * "shrink it to about 1/4th the radius, in the cockpit view only"), and a
   * quarter-radius disc reads as the prop without owning the picture. External
   * cameras always draw the measured radius.
   */
  readonly cockpitRadiusScale?: number;
  /**
   * Disc-centre height (hub frame, metres up from the pose origin) while the
   * cockpit camera is up. The measured hub sits well below the pilot's eye —
   * on the Camel 0.26 m against an eye at 0.89 m — so a quarter-radius disc
   * at the true hub disappears entirely behind the dashboard art (user
   * report, 2026-08-21). Raising the cockpit disc onto the sight line keeps
   * the shimmer in the windshield; external views always use the true hub.
   */
  readonly cockpitUpM?: number;
}

/** §4's cockpit radius factor — see `PropellerSource.cockpitRadiusScale`. Iterated live with the user (2026-08-21): 0.25 → 0.30 → 0.36. */
export const PROP_RADIUS_COCKPIT_SCALE = 0.36;

/**
 * How far below the sight line the cockpit disc's centre sits, in metres —
 * "the center needs to be at the nose of the plane, not the center of the gun
 * view" (user, 2026-08-21). This was first a fraction of the drawn radius,
 * which made the drop differ per airframe — the Albatros's smaller disc got a
 * smaller drop and sat back in the gunsight (user report). An absolute drop
 * below the eye gives every cockpit the same picture.
 */
export const PROP_COCKPIT_SIGHT_DROP_M = 0.28;

/**
 * Where the two disc meshes sit in the transparent pass.
 *
 * A named constant rather than a literal because it is one end of a comparison
 * made elsewhere: 3D COCKPIT's dashboard quad has to draw **after** this or the
 * propeller paints over the painted panel (`scene-cockpit-3d.ts`'s
 * `DASH_QUAD_RENDER_ORDER`, and the user report behind it). The number itself is
 * unchanged and the disc's own reason for having it is unchanged too — see the
 * comment at the assignment.
 */
export const PROP_DISC_RENDER_ORDER = 1;

const scratchMatrix = new THREE.Matrix4();
const scratchPosition = new THREE.Vector3();
const scratchQuaternion = new THREE.Quaternion();
const scratchScale = new THREE.Vector3();
const scratchOffset = new THREE.Vector3();

/**
 * The disc lies in the geometry's XY plane with its normal on +Z, and the
 * aircraft group's frame is the engine's own body frame (x right, y up, z aft
 * — `flight/cockpit-view.ts`), so an unrotated disc already faces along the
 * nose axis. Only the hub offset has to be rotated into world space.
 */
function writeInstance(
  mesh: THREE.InstancedMesh,
  index: number,
  pose: Pose,
  hub: PropellerHub,
  radiusScale: number,
  upM: number,
): void {
  scratchQuaternion.set(pose.orientation.x, pose.orientation.y, pose.orientation.z, pose.orientation.w);
  scratchOffset.set(0, upM, -hub.forwardM).applyQuaternion(scratchQuaternion);
  scratchPosition.set(pose.position.x + scratchOffset.x, pose.position.y + scratchOffset.y, pose.position.z + scratchOffset.z);
  const radius = hub.radiusM * radiusScale;
  scratchScale.set(radius, radius, 1);
  scratchMatrix.compose(scratchPosition, scratchQuaternion, scratchScale);
  mesh.setMatrixAt(index, scratchMatrix);
}

export interface PropellerDiscs {
  /** Add this to the scene once. */
  readonly group: THREE.Group;
  /** Turns the whole effect on or off — AD-012 §6's `PROPELLER EFFECT` setting. */
  setEnabled(enabled: boolean): void;
  /**
   * Places every aeroplane's disc for this frame and advances each one's
   * integrated phase by `dtS`. `cockpit` selects AD-012 §4's alpha cap.
   */
  update(dtS: number, sources: readonly PropellerSource[], cockpit: boolean): void;
  /** How many discs the last `update` published — what a draw-call/coverage check reads. */
  readonly liveCount: number;
  dispose(): void;
}

interface DiscState {
  readonly group: THREE.Group;
  readonly discMesh: THREE.InstancedMesh;
  readonly bladeMesh: THREE.InstancedMesh;
  readonly materials: PropDiscMaterials;
  readonly phases: THREE.InstancedBufferAttribute;
  readonly rpms: THREE.InstancedBufferAttribute;
  /** Integrated rotation angle per aeroplane id, so a disc keeps turning smoothly across frames. */
  readonly phaseById: Map<string, number>;
  /** `modelId` → its measured hub, or `null` while the measurement is in flight. */
  readonly hubs: Map<string, PropellerHub | null>;
  enabled: boolean;
  written: number;
  disposed: boolean;
}

function instancedAttribute(): THREE.InstancedBufferAttribute {
  const attribute = new THREE.InstancedBufferAttribute(new Float32Array(DISC_CAPACITY), 1);
  attribute.setUsage(THREE.DynamicDrawUsage);
  return attribute;
}

/** The hub for `modelId`, kicking off its one-per-document measurement on first sight. */
function hubFor(state: DiscState, modelId: string): PropellerHub | null {
  const known = state.hubs.get(modelId);
  if (known !== undefined) return known;
  state.hubs.set(modelId, null);
  void propellerHubOf(modelId).then((hub) => {
    if (!state.disposed && hub) state.hubs.set(modelId, hub);
  });
  return null;
}

/** Advances one aeroplane's phase and returns it, wrapped so the accumulator never loses precision. */
function advancePhase(state: DiscState, source: PropellerSource, dtS: number): number {
  const phase = (state.phaseById.get(source.id) ?? 0) + propellerPhaseStep(source.rpmFraction, dtS);
  const wrapped = phase % (Math.PI * 2);
  state.phaseById.set(source.id, wrapped);
  return wrapped;
}

function writeFrame(state: DiscState, dtS: number, sources: readonly PropellerSource[], cockpit: boolean): void {
  let written = 0;
  for (const source of sources) {
    if (written >= DISC_CAPACITY) break;
    if (!source.modelId || !hasNoseProp(source.modelId)) continue;
    const hub = hubFor(state, source.modelId);
    if (!hub) continue;
    const radiusScale = cockpit ? (source.cockpitRadiusScale ?? 1) : 1;
    const upM =
      cockpit && source.cockpitUpM !== undefined ? source.cockpitUpM - PROP_COCKPIT_SIGHT_DROP_M : hub.upM;
    writeInstance(state.discMesh, written, source.pose, hub, radiusScale, upM);
    writeInstance(state.bladeMesh, written, source.pose, hub, radiusScale, upM);
    state.phases.setX(written, advancePhase(state, source, dtS));
    state.rpms.setX(written, Math.min(1, Math.max(0, source.rpmFraction)));
    written += 1;
  }
  state.written = written;
}

function publish(state: DiscState): void {
  for (const mesh of [state.discMesh, state.bladeMesh]) {
    mesh.count = state.written;
    mesh.visible = state.enabled && state.written > 0;
    mesh.instanceMatrix.needsUpdate = true;
  }
  state.phases.needsUpdate = true;
  state.rpms.needsUpdate = true;
}

/**
 * Builds the pair. `palette` is `BARON.PAL` as every other model path reads it;
 * `undefined` (a palette that failed to fetch) is not a case this handles —
 * the caller skips the whole effect, exactly as `aircraft-mesh.ts` falls back
 * to flat colours rather than failing the flight.
 */
export function createPropellerDiscs(palette: BakedPaletteBody): PropellerDiscs {
  const materials = createPropDiscMaterials(palette);
  const geometry = new THREE.CircleGeometry(1, DISC_SEGMENTS);
  const phases = instancedAttribute();
  const rpms = instancedAttribute();
  geometry.setAttribute(PROP_PHASE_ATTRIBUTE, phases);
  geometry.setAttribute(PROP_RPM_ATTRIBUTE, rpms);

  const group = new THREE.Group();
  const discMesh = new THREE.InstancedMesh(geometry, materials.disc, DISC_CAPACITY);
  const bladeMesh = new THREE.InstancedMesh(geometry, materials.blades, DISC_CAPACITY);
  const state: DiscState = {
    group,
    discMesh,
    bladeMesh,
    materials,
    phases,
    rpms,
    phaseById: new Map(),
    hubs: new Map(),
    enabled: true,
    written: 0,
    disposed: false,
  };
  for (const mesh of [discMesh, bladeMesh]) {
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    mesh.frustumCulled = false;
    mesh.count = 0;
    mesh.visible = false;
    // Drawn after the opaque pass and after the bullet quads: a translucent
    // surface hung on the nose of an aeroplane has to blend over whatever the
    // aeroplane already put in the framebuffer.
    mesh.renderOrder = PROP_DISC_RENDER_ORDER;
    group.add(mesh);
  }

  return {
    group,
    setEnabled(enabled) {
      state.enabled = enabled;
      if (!enabled) publish(state);
    },
    update(dtS, sources, cockpit) {
      if (state.disposed) return;
      materials.setViewAlpha(
        cockpit ? DISC_ALPHA_COCKPIT : DISC_ALPHA_EXTERNAL,
        cockpit ? BLADE_ALPHA_COCKPIT : BLADE_ALPHA_EXTERNAL,
      );
      if (state.enabled) writeFrame(state, dtS, sources, cockpit);
      else state.written = 0;
      publish(state);
    },
    get liveCount() {
      return state.written;
    },
    dispose() {
      state.disposed = true;
      geometry.dispose();
      materials.dispose();
      state.phaseById.clear();
      state.hubs.clear();
    },
  };
}
